import { serviceUrls } from '../../config/serviceUrls';
import { protectedFetch } from '../../utils/authSession';
import type {
  ReadingContentPayload,
  ReadingContextType,
  ReadingTutorContextResponse,
  ReadingTutorEntryPoint,
  ReadingTutorLaunchMode,
} from './reading.types';

const READING_BASE = `${serviceUrls.gateway.replace(/\/$/, '')}/v1/reading-comprehension`;

export class ReadingApiError extends Error {
  readonly status: number;
  readonly bodyText: string;

  constructor(message: string, status: number, bodyText: string) {
    super(message);
    this.name = 'ReadingApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

function resolveAudioUrl(url: string | null | undefined): string | null {
  const raw = String(url || '').trim();
  if (!raw) return null;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const gateway = serviceUrls.gateway.replace(/\/$/, '');
  return `${gateway}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

export async function fetchReadingParagraphAudioBlob(apiUrl: string): Promise<Blob> {
  const res = await protectedFetch(apiUrl, {
    headers: {
      Accept: 'audio/mp4,audio/*,*/*',
    },
  });
  if (!res.ok) {
    throw new Error(`Audio unavailable (${res.status})`);
  }
  const blob = await res.blob();
  const contentType = (blob.type || res.headers.get('content-type') || '').toLowerCase();
  if (
    blob.size === 0 ||
    (contentType &&
      !contentType.startsWith('audio/') &&
      contentType !== 'application/octet-stream')
  ) {
    throw new Error('Invalid audio response');
  }
  return blob;
}

const CURRENT_READING_TTL_MS = 5 * 60 * 1000;

let currentReadingCache: { at: number; data: ReadingContentPayload } | null = null;
let currentReadingInflight: Promise<ReadingContentPayload> | null = null;

export function peekCachedCurrentReading(): ReadingContentPayload | null {
  if (!currentReadingCache) return null;
  if (Date.now() - currentReadingCache.at > CURRENT_READING_TTL_MS) return null;
  return currentReadingCache.data;
}

export function resetCurrentReadingCacheForTests(): void {
  currentReadingCache = null;
  currentReadingInflight = null;
}

function normalizeCurrentReading(data: ReadingContentPayload): ReadingContentPayload {
  return {
    ...data,
    page_preview: data.page_preview
      ? {
          ...data.page_preview,
          url: resolveAudioUrl(data.page_preview.url),
        }
      : null,
    hero_preview: data.hero_preview
      ? {
          ...data.hero_preview,
          url: resolveAudioUrl(data.hero_preview.url),
        }
      : null,
    hero_scenes: data.hero_scenes
      ? {
          ...data.hero_scenes,
          traditional: {
            ...data.hero_scenes.traditional,
            url: resolveAudioUrl(data.hero_scenes.traditional.url),
          },
          contemporary: {
            ...data.hero_scenes.contemporary,
            url: resolveAudioUrl(data.hero_scenes.contemporary.url),
          },
        }
      : null,
    artists: data.artists?.map((artist) => ({
      ...artist,
      scene_image: artist.scene_image
        ? {
            ...artist.scene_image,
            url: resolveAudioUrl(artist.scene_image.url),
          }
        : null,
    })),
    paragraphs: data.paragraphs.map((p) => ({
      ...p,
      audio: {
        ...p.audio,
        url: resolveAudioUrl(p.audio?.url),
      },
    })),
  };
}

async function loadCurrentReading(): Promise<ReadingContentPayload> {
  const res = await protectedFetch(`${READING_BASE}/readings/current`);
  if (!res.ok) {
    const text = await res.text();
    throw new ReadingApiError(
      text || `Reading content unavailable (${res.status})`,
      res.status,
      text,
    );
  }
  const data = (await res.json()) as ReadingContentPayload;
  const normalized = normalizeCurrentReading(data);
  currentReadingCache = { at: Date.now(), data: normalized };
  return normalized;
}

export async function fetchCurrentReading(): Promise<ReadingContentPayload> {
  const cached = peekCachedCurrentReading();
  if (cached) return cached;
  if (currentReadingInflight) return currentReadingInflight;
  currentReadingInflight = loadCurrentReading().finally(() => {
    currentReadingInflight = null;
  });
  return currentReadingInflight;
}

export async function createReadingTutorContext(input: {
  readingKey: string;
  launchMode: ReadingTutorLaunchMode;
  entryPoint: ReadingTutorEntryPoint;
  requiresPersonaChoice: boolean;
  contextType: ReadingContextType;
  paragraphId?: string | null;
  selectedText?: string | null;
  dictionaryWord?: string | null;
  questionId?: string | null;
}): Promise<ReadingTutorContextResponse> {
  const res = await protectedFetch(
    `${READING_BASE}/readings/${encodeURIComponent(input.readingKey)}/tutor-contexts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        launch_mode: input.launchMode,
        entry_point: input.entryPoint,
        requires_persona_choice: input.requiresPersonaChoice,
        context_type: input.contextType,
        paragraph_id: input.paragraphId ?? null,
        selected_text: input.selectedText ?? null,
        dictionary_word: input.dictionaryWord ?? null,
        question_id: input.questionId ?? null,
      }),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Tutor context failed (${res.status})`);
  }
  return res.json() as Promise<ReadingTutorContextResponse>;
}
